import Cookies from "js-cookie";
import React, { createContext, useContext, useState } from "react";

const COOKIE_POLICY_URL = "https://unbiodiversitylab.org/privacy-policy";

export interface ICookieConsentContext {
  acceptAll: () => void;
  closeDialog: () => void;
  cookieOptions: {
    domain: string;
    expires: number;
    secure: boolean;
    sameSite: "Strict" | "Lax" | "None";
  };
  externalCookiePolicyUrl: string | false;
  isDialogOpen: boolean;
  isDrawerOpen: boolean;
  reviewResponse: (e?) => void;
  openDialog: (e?) => void;
  readMore: (e?) => void;
  rejectAll: (e?) => void;
  rejectsAllTracking: boolean;
  setIsDialogOpen: (boolean) => void;
  setIsDrawerOpen: (boolean) => void;
}

export interface ICookieConsentContextProviderProps {
  children: React.ReactNode;
  cookieName: string;
  cookieDomain?: string;
  cookieExpiresInDays?: number;
  cookieSecure?: boolean;
  cookieSameSite?: "Strict" | "Lax" | "None";
  externalCookiePolicyUrl?: string | false;
  onAccept?: () => void;
  onReject?: () => void;
}

/**
 * This is a container for a simple consent banner, which allows a user
 * to accept or reject tracking cookies (and similar technologies).
 *
 * It coordinates the following components, responsible for providing the
 * user with controls and information:
 *
 * - CookieConsentDrawer
 * - CookieConsentDialog
 *
 * Additional future work might include:
 *
 * - finer-grained preference setting, if we develop use cases for it
 * - invalidating consent already given in circumstances where the basis for
 *   consent may have changed
 */
export const CookieConsentContext = createContext<ICookieConsentContext>(null);
export const useCookieConsentContext = () => useContext(CookieConsentContext);

export const CookieConsentContextProvider = (
  props: ICookieConsentContextProviderProps
) => {
  const {
    children,
    cookieName,
    cookieDomain,
    cookieExpiresInDays = 365,
    cookieSecure = true,
    cookieSameSite = "Strict",
    externalCookiePolicyUrl = COOKIE_POLICY_URL,
    onAccept = () => {},
    onReject = () => {},
  } = props;

  if (!cookieName) {
    return null;
  }

  const cookieOptions = {
    domain: cookieDomain,
    expires: cookieExpiresInDays,
    secure: cookieSecure,
    sameSite: cookieSameSite,
  };

  const [rejectsAllTracking] = useState(
    Boolean(
      // globalPrivacyControl is not official but if it exists, then we
      // should probably respect it.
      (navigator as Navigator & { globalPrivacyControl?: any })
        ?.globalPrivacyControl || navigator?.doNotTrack
    )
  );

  const getHasSetCookieChoices = () =>
    ["true", "false"].includes(Cookies.get(cookieName));

  const [isDrawerOpen, setIsDrawerOpen] = useState(
    !getHasSetCookieChoices() && !rejectsAllTracking
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const acceptAll = () => {
    setIsDrawerOpen(false);
    setIsDialogOpen(false);
    Cookies.set(cookieName, "true", cookieOptions);
    onAccept();
  };

  const rejectAll = () => {
    setIsDrawerOpen(false);
    setIsDialogOpen(false);
    Cookies.set(cookieName, "false", cookieOptions);
    onReject();
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const readMore = () => {
    if (COOKIE_POLICY_URL) {
      window.open(COOKIE_POLICY_URL, "_blank");
    } else {
      openDialog();
    }
  };

  const reviewResponse = () => {
    if (COOKIE_POLICY_URL) {
      setIsDrawerOpen(true);
    } else {
      openDialog();
    }
  };

  return (
    <CookieConsentContext.Provider
      value={{
        acceptAll,
        closeDialog,
        cookieOptions,
        externalCookiePolicyUrl,
        isDialogOpen,
        isDrawerOpen,
        openDialog,
        readMore,
        reviewResponse,
        rejectAll,
        rejectsAllTracking,
        setIsDialogOpen,
        setIsDrawerOpen,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
